import type { StayPlanModule } from "./stayPlans";

export interface StayPlanChargeLink {
  saleId: string;
  dueOn: string;
  module: StayPlanModule;
  contractId: string;
  petId: string | null;
  planName: string;
}

export interface ReceivableOrigin {
  kind: "stay_plan";
  module: StayPlanModule;
  contractId: string;
  petId: string | null;
  planName: string;
}

/** Anexa a origem do plano a qualquer linha identificada por venda, sem exigir vencimento nem ordenação. */
export function attachReceivableOrigins<T extends { saleId: string }>(
  rows: T[],
  links: Map<string, StayPlanChargeLink>,
): (T & { origin: ReceivableOrigin | null })[] {
  return rows.map((row) => {
    const link = links.get(row.saleId) ?? null;
    return {
      ...row,
      origin: link
        ? { kind: "stay_plan" as const, module: link.module, contractId: link.contractId, petId: link.petId, planName: link.planName }
        : null,
    };
  });
}

export interface ReceivableDecoration {
  dueOn: string | null;
  origin: ReceivableOrigin | null;
  isOverdue: boolean;
}

/**
 * Sem vencimento a lista ordenava por `created_at`, o que misturava a
 * mensalidade pós-paga (vence só no fim do ciclo) com contas já vencidas.
 */
export function decorateReceivables<T extends { saleId: string; status: string; createdAt: string }>(
  rows: T[],
  links: Map<string, StayPlanChargeLink>,
  today: string,
): (T & ReceivableDecoration)[] {
  return rows
    .map((row) => {
      const link = links.get(row.saleId) ?? null;
      return {
        ...row,
        dueOn: link?.dueOn ?? null,
        origin: link
          ? { kind: "stay_plan" as const, module: link.module, contractId: link.contractId, petId: link.petId, planName: link.planName }
          : null,
        // Só a partir do dia seguinte: vencer hoje ainda está no prazo.
        isOverdue: Boolean(link) && link!.dueOn < today && row.status !== "PAGA",
      };
    })
    .sort((a, b) => {
      if (a.dueOn && b.dueOn) return a.dueOn.localeCompare(b.dueOn) || a.createdAt.localeCompare(b.createdAt);
      if (a.dueOn) return -1;
      if (b.dueOn) return 1;
      return a.createdAt.localeCompare(b.createdAt);
    });
}
